import { type FC, useMemo } from "react";

import { cx } from "@emotion/css";
import { Grid, LineChart, Threshold, Tooltip, XAxis, YAxis } from "@snapchat/snap-design-system-charts";

import { formatNumbers } from "../../../lens-stats/formatters";
import { convertChartLabelNumber } from "../../converters";
import { areStatsEmpty } from "../../derived-data";
import { formatTooltipContent, formatTooltipTitle } from "../../formatters";
import { ChartDimensions, type InsightsMetrics, type StatsItem } from "../../models";

import { chartContainer, emptyChartContainer } from "./charts.styles";

interface LineChartComponentProps {
    data: InsightsMetrics<StatsItem>;
    contentName: string;
    yLabelConverter?: (label: string) => string;
    formatter?: (value: number | string) => string;
    yAxisWidth?: number;
}

export const LineChartComponent: FC<LineChartComponentProps> = ({
    data,
    contentName,
    yAxisWidth,
    formatter = formatNumbers,
    yLabelConverter = convertChartLabelNumber,
}) => {
    const formatContent = useMemo(() => formatTooltipContent(contentName, formatter), [contentName, formatter]);
    const isEmpty = useMemo(() => areStatsEmpty<StatsItem>(data.stats), [data]);
    return (
        <div className={cx(chartContainer, isEmpty && emptyChartContainer)}>
            <LineChart
                data={[...data.stats]}
                width={1040}
                height={250}
                x={ChartDimensions.key}
                y={ChartDimensions.value}
            >
                <Grid strokeDasharray="1 6" vertical={true} />
                <XAxis labelOrientation="vertical" interval={0} />
                <YAxis label={yLabelConverter} width={yAxisWidth} />
                {!isEmpty && <Tooltip title={formatTooltipTitle} content={formatContent} />}
                <Threshold value={data.averageValue} />
            </LineChart>
        </div>
    );
};
